import {
  AdminSearchMyExamCenterProctorRequestsStep,
  ExamCenterProctorRequestVerdictStep,
  useAdminSearchMyExamCenterProctorRequestsQuery,
  Verdict,
} from '@exam-center/graphql';
import { useNavigate } from '@exam-center/router';
import {
  DescriptionOutlinedIcon,
  NoRowsOverlay,
  type GridEventListener,
  type GridRowParams,
} from '@shared/components';

import {
  BaseAdminProctorRequestList,
  rejectedColumns,
  type RejectedProctorRequest,
} from '../../components';
import { ORDER_TYPE, PROCTOR_TYPE_VARIANT_MAP } from '../../constants';
import { useAdminProctorRequestListSearchParams } from '../../hooks';

export default function RejectedAdminProctorRequestList() {
  const [
    { page, pageSize, sortOrder, proctorType, examCenter, search },
    { changePageNumber, changePageSize, changeSorting },
  ] = useAdminProctorRequestListSearchParams();
  const navigate = useNavigate();

  const { data, loading } = useAdminSearchMyExamCenterProctorRequestsQuery({
    variables: {
      adminSearchExamCenterProctorRequestsInput: {
        filter: {
          step: AdminSearchMyExamCenterProctorRequestsStep.Rejected,
          fullName: search,
          ...(examCenter.length > 0 ? { examCenterCodes: examCenter } : {}),
          ...(proctorType.length > 0 ? { types: proctorType } : {}),
        },
        paging: {
          currentPage: page,
          pageSize: pageSize,
        },
        order: {
          field: 'verdicts.submittedAt',
          type: ORDER_TYPE[sortOrder],
        },
      },
    },
  });

  const examCenterProctorRequests =
    data?.ecAdminSearchMyExamCenterProctorRequests.examCenterProctorRequests ?? [];

  const totalCount = data?.ecAdminSearchMyExamCenterProctorRequests.total ?? 0;

  const handleRowClick: GridEventListener<'rowClick'> = (
    params: GridRowParams<RejectedProctorRequest>,
  ) => {
    navigate(`/admin/exam-proctors/requests/:requestId`, {
      params: {
        requestId: params.row.id,
      },
    });
  };

  const examCenterProctorRequestItems = examCenterProctorRequests.map((item) => {
    const latestRejectedVerdict = item.verdicts
      ?.filter((v) => v.verdict === Verdict.Rejected)
      .sort(
        (a, b) => new Date(b.submittedAt ?? 0).getTime() - new Date(a.submittedAt ?? 0).getTime(),
      )[0];

    const latestRequestedAt = latestRejectedVerdict?.requestedAt;

    const latestVerdicts =
      item.verdicts?.filter((v) => v.requestedAt.getTime() === latestRequestedAt.getTime()) ?? [];

    let firstReviewVerdict = null;
    let secondReviewVerdict = null;

    if (latestVerdicts.length >= 2) {
      firstReviewVerdict =
        latestVerdicts.find((v) => v.step === ExamCenterProctorRequestVerdictStep.InReview_1)
          ?.verdict ?? null;
      secondReviewVerdict =
        latestVerdicts.find((v) => v.step === ExamCenterProctorRequestVerdictStep.InReview_2)
          ?.verdict ?? null;
    } else {
      firstReviewVerdict =
        latestVerdicts.find((v) => v.step === ExamCenterProctorRequestVerdictStep.InReview_1)
          ?.verdict ?? null;
    }

    return {
      id: item.id,
      refCode: item.refCode,
      name: [item.titleTh, item.firstNameTh, item.middleNameTh, item.lastNameTh]
        .filter(Boolean)
        .join(' '),
      type: PROCTOR_TYPE_VARIANT_MAP[item.type],
      status: 'rejected',
      rejectedAt: latestRejectedVerdict?.submittedAt,
      examCenter: item.examCenterCode,
      firstReviewVerdict: firstReviewVerdict,
      secondReviewVerdict: secondReviewVerdict,
    };
  });
  return (
    <BaseAdminProctorRequestList
      columns={rejectedColumns}
      loading={loading}
      rows={examCenterProctorRequestItems}
      sortingMode='server'
      slots={{
        noRowsOverlay: () => (
          <NoRowsOverlay
            title='ยังไม่มีคำขอผู้คุมสอบที่ไม่อนุมัติ'
            icon={DescriptionOutlinedIcon}
          />
        ),
      }}
      slotProps={{
        footer: {
          total: totalCount,
          pagination: {
            page: page,
            count: totalCount,
            rowsPerPage: pageSize,
            onPageChange: (_, page) => {
              changePageNumber(page);
            },
            onRowsPerPageChange: (event) => {
              changePageSize(parseInt(event.target.value));
            },
          },
        },
      }}
      onRowClick={handleRowClick}
      onSortModelChange={(sorting) => {
        const sortModel = sorting[0];
        if (sortModel?.field && sortModel?.sort) {
          return changeSorting({
            sortOrder: sortModel.sort,
            sortBy: sortModel.field,
          });
        }

        return changeSorting({
          sortOrder: null,
          sortBy: null,
        });
      }}
    />
  );
}
